import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { SlideshowLightbox } from "lightbox.js-react";

const TaskScheduler = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [taskImages, setTaskImages] = useState({});
  const [selectedTaskForGallery, setSelectedTaskForGallery] = useState(null);
  const [selectedTaskImages, setSelectedTaskImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showGalleryLightbox, setShowGalleryLightbox] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    taskNumber: "",
    siteName: "",
    status: "",
  });

  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [formData, setFormData] = useState({
    site: "",
    customer_id: null,
    task: "",
    description: "",
    date_from: "",
    date_to: "",
    status: "undone",
    image: null,
  });

  // Load tasks from API only once on component mount
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/tasks`);
        const tasksData = Array.isArray(response.data) ? response.data : [];
        setTasks(tasksData);
        setFilteredTasks(tasksData);
      } catch (err) {
        console.warn(
          "Failed to fetch tasks from API, using localStorage:",
          err.message,
        );
        // Fallback to localStorage
        const savedTasks = localStorage.getItem("tasks");
        const parsedTasks = savedTasks ? JSON.parse(savedTasks) : [];
        setTasks(parsedTasks);
        setFilteredTasks(parsedTasks);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchTasks();
  }, [apiUrl]);

  // Apply filters whenever tasks or filters change
  useEffect(() => {
    if (isInitialLoad) return; // Don't filter during initial load

    let result = [...tasks];

    // Filter by task number
    if (filters.taskNumber.trim()) {
      result = result.filter((task) =>
        task.task_number
          ?.toLowerCase()
          .includes(filters.taskNumber.toLowerCase()),
      );
    }

    // Filter by site name (customer name)
    if (filters.siteName.trim()) {
      result = result.filter((task) =>
        task.customer?.name
          ?.toLowerCase()
          .includes(filters.siteName.toLowerCase()),
      );
    }

    // Filter by status
    if (filters.status) {
      result = result.filter((task) => task.status === filters.status);
    }

    setFilteredTasks(result);
  }, [tasks, filters, isInitialLoad]);

  // Sync tasks to localStorage whenever tasks change
  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem("tasks", JSON.stringify(tasks));
    }
  }, [tasks, isInitialLoad]);

  // Fetch customers from API only once
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/customers`);
        setCustomers(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.warn("Failed to fetch customers from API:", err.message);
        setCustomers([]);
      }
    };

    fetchCustomers();
  }, [apiUrl]);

  // Fetch task images counts only once when tasks are loaded
  useEffect(() => {
    const fetchTaskImages = async () => {
      if (tasks.length === 0 || isInitialLoad) return;

      try {
        const imagesCounts = {};

        // Initialize all tasks with 0 count
        tasks.forEach((task) => {
          imagesCounts[task.id] = 0;
        });

        // Fetch all task images at once
        const response = await axios.get(`${apiUrl}/api/task-img`);
        const allImages = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];

        // Group images by task_id and count them
        allImages.forEach((image) => {
          if (image.task_id) {
            imagesCounts[image.task_id] =
              (imagesCounts[image.task_id] || 0) + 1;
          }
        });

        setTaskImages(imagesCounts);
      } catch (err) {
        console.warn("Failed to fetch task images:", err.message);
      }
    };

    fetchTaskImages();
  }, [tasks, apiUrl, isInitialLoad]);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for site selection to capture customer_id
    if (name === "site") {
      const selectedCustomer = customers.find((c) => c.name === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        customer_id: selectedCustomer ? selectedCustomer.id : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      taskNumber: "",
      siteName: "",
      status: "",
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.site || !formData.task) {
      alert("Please fill in all required fields (Site, Task)");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare FormData for file upload
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("site", formData.site);
      formDataToSubmit.append("customer_id", formData.customer_id);
      formDataToSubmit.append("task", formData.task);
      formDataToSubmit.append("description", formData.description);
      formDataToSubmit.append("date_from", formData.date_from);
      formDataToSubmit.append("date_to", formData.date_to);
      formDataToSubmit.append("status", formData.status);

      // Add image if provided
      if (formData.image) {
        formDataToSubmit.append("image", formData.image);
      }

      const response = await axios.post(
        `${apiUrl}/api/tasks`,
        formDataToSubmit,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // Get the new task from response
      const newTask = response.data;

      // Find the customer details from the customers list
      const selectedCustomer = customers.find(
        (c) => c.id === formData.customer_id,
      );

      // Attach the full customer object to the new task
      const newTaskWithCustomer = {
        ...newTask,
        customer: selectedCustomer || {
          name: formData.site,
          id: formData.customer_id,
        },
      };

      // Update local state with new task (no refetch)
      setTasks((prev) => {
        const updatedTasks = [...prev, newTaskWithCustomer];
        return updatedTasks;
      });

      setSuccess("Task saved successfully!");

      // Reset form on success
      setFormData({
        site: "",
        customer_id: null,
        task: "",
        description: "",
        date_from: "",
        date_to: "",
        status: "undone",
        image: null,
      });
      setShowForm(false);
    } catch (err) {
      console.error("Error creating task:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save task. Please check your API connection and try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await axios.delete(`${apiUrl}/api/tasks/${taskId}`);

        // Update local state by removing the deleted task (no refetch)
        setTasks((prev) => {
          const updatedTasks = prev.filter((task) => task.id !== taskId);
          return updatedTasks;
        });

        setSuccess("Task deleted successfully");
      } catch (err) {
        console.error("Failed to delete task:", err.message);
        setError("Failed to delete task. Please try again.");
      }
    }
  };

  const toggleStatus = async (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updatedStatus = task.status === "done" ? "undone" : "done";

    // Create updated task while preserving the customer data
    const updatedTaskData = {
      ...task,
      status: updatedStatus,
    };

    try {
      const response = await axios.put(
        `${apiUrl}/api/mark-as-done/${taskId}`,
        updatedTaskData,
      );

      // Preserve the customer data from the original task if the response doesn't have it
      const updatedTaskWithCustomer = {
        ...response.data,
        customer: response.data.customer || task.customer,
      };

      // Update local state with the updated task (no refetch)
      setTasks((prev) => {
        const updatedTasks = prev.map((task) =>
          task.id === taskId ? updatedTaskWithCustomer : task,
        );
        return updatedTasks;
      });

      setSuccess("Task status updated successfully");
    } catch (err) {
      console.error("Failed to update task status:", err.message);
      setError("Failed to update task status. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatCreatedDate = (task) => {
    const createdDate = task.created_date || task.createdAt || task.created_at;
    if (!createdDate) return "N/A";
    return new Date(createdDate).toLocaleDateString();
  };

  const openImageGallery = async (taskId) => {
    setSelectedTaskForGallery(taskId);
    setGalleryLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/task-img`);
      const allImages = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      // Filter images for the selected task
      const taskImagesList = allImages.filter(
        (image) => image.task_id === taskId,
      );
      setSelectedTaskImages(taskImagesList);
    } catch (err) {
      console.error("Failed to fetch task images for gallery:", err.message);
      setError("Failed to load images. Please try again.");
    } finally {
      setGalleryLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (apiUrl.startsWith("http://")) return `${apiUrl}/storage/${imagePath}`;

    if (apiUrl.startsWith("https://"))
      return `${apiUrl}/public/storage/${imagePath}`;

    return imagePath;
  };

  // Get unique site names for filter dropdown
  const uniqueSiteNames = [
    ...new Set(tasks.map((task) => task.customer?.name).filter(Boolean)),
  ];

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-green-800">{success}</p>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 hover:text-green-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Task Scheduler</h2>
            <p className="text-gray-600 mt-1">
              Manage your construction tasks and projects
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <FunnelIcon className="w-5 h-5" />
              <span>Filters</span>
              {(filters.taskNumber || filters.siteName || filters.status) && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-[#23A19B] text-white rounded-full">
                  Active
                </span>
              )}
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-[#23A19B] text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Task</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
              Filter Tasks
            </h3>
            <button
              onClick={clearFilters}
              className="text-sm text-[#23A19B] hover:text-emerald-700"
            >
              Clear All Filters
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filter by Task Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Number
              </label>
              <input
                type="text"
                name="taskNumber"
                value={filters.taskNumber}
                onChange={handleFilterChange}
                placeholder="Search by task number..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#23A19B]"
              />
            </div>

            {/* Filter by Site Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Name
              </label>
              <select
                name="siteName"
                value={filters.siteName}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#23A19B]"
              >
                <option value="">All Sites</option>
                {uniqueSiteNames.map((siteName) => (
                  <option key={siteName} value={siteName}>
                    {siteName}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter by Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#23A19B]"
              >
                <option value="">All Status</option>
                <option value="undone">Pending</option>
                <option value="done">Completed</option>
              </select>
            </div>
          </div>

          {/* Filter Results Summary */}
          {(filters.taskNumber || filters.siteName || filters.status) && (
            <div className="mt-4 pt-3 border-t text-sm text-gray-600">
              Found {filteredTasks.length} task
              {filteredTasks.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}

      {/* Add Task Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Add New Task
          </h3>
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site *
              </label>
              <select
                name="site"
                value={formData.site}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                <option value="">Select a customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.name}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task *
              </label>
              <input
                type="text"
                name="task"
                value={formData.task}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., Foundation work"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Detailed description of the task..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Image (Optional)
              </label>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {formData.image && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ {formData.image.name} selected
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                name="date_from"
                value={formData.date_from}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                name="date_to"
                value={formData.date_to}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="md:col-span-2 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#23A19B] text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4" />
                    <span>Add Task</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks List - Notice Board Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTasks.length === 0 ? (
          <div className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {tasks.length === 0 ? "No tasks yet" : "No matching tasks found"}
            </h3>
            <p className="text-gray-500">
              {tasks.length === 0
                ? "Create your first task to get started"
                : "Try adjusting your filters to see more results"}
            </p>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div
              key={task.id}
              className={`bg-white rounded-lg shadow-sm border-l-4 p-6 relative ${
                task.status === "done"
                  ? "border-l-green-500 bg-green-50"
                  : "border-l-orange-500 bg-orange-50"
              }`}
            >
              {/* Status Badge */}
              <div className="absolute top-4 right-4 flex items-center space-x-2">
                <button
                  onClick={() => toggleStatus(task.id)}
                  className={`p-1 rounded-full ${
                    task.status === "done"
                      ? "text-green-600 hover:bg-green-100"
                      : "text-orange-600 hover:bg-orange-100"
                  }`}
                >
                  {task.status === "done" ? (
                    <CheckCircleIcon className="w-6 h-6" />
                  ) : (
                    <XCircleIcon className="w-6 h-6" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(task.id)}
                  className="p-1 rounded-full text-red-600 hover:bg-red-100"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Task Content */}
              <div className="pr-16">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm font-semibold text-gray-500">
                    #{task.task_number}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      task.status === "done"
                        ? "bg-green-100 text-green-800"
                        : "bg-orange-100 text-orange-800"
                    }`}
                  >
                    {task.status === "done" ? "Completed" : "Pending"}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {task.task}
                </h3>

                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <p>
                    <span className="font-medium">Site:</span>{" "}
                    {task.customer?.name || "N/A"}
                  </p>
                  {task.description && (
                    <p>
                      <span className="font-medium">Description:</span>{" "}
                      {task.description}
                    </p>
                  )}
                </div>

                {task.image && (
                  <div className="mb-3">
                    <SlideshowLightbox>
                      <img
                        src={getImageUrl(task.image)}
                        alt="Task"
                        className="w-full h-40 object-cover rounded-md border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() =>
                          setSelectedImage(getImageUrl(task.image))
                        }
                        onError={(e) => {
                          console.warn(
                            "Failed to load image:",
                            getImageUrl(task.image),
                          );
                          e.target.style.display = "none";
                        }}
                      />
                    </SlideshowLightbox>
                  </div>
                )}

                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <div className="flex space-x-4">
                    <p>
                      <span className="font-medium">From:</span>{" "}
                      {formatDate(task.date_from)}
                    </p>
                    <p>
                      <span className="font-medium">To:</span>{" "}
                      {formatDate(task.date_to)}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-gray-400 flex items-center justify-between">
                  <span>Created: {formatCreatedDate(task)}</span>
                  {taskImages[task.id] > 0 && (
                    <button
                      onClick={() => openImageGallery(task.id)}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                    >
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {taskImages[task.id]}{" "}
                      {taskImages[task.id] === 1 ? "image" : "images"}
                    </button>
                  )}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Single Image Preview Modal */}
      {selectedImage && !selectedTaskForGallery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-screen overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Image Preview
              </h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="p-4 flex justify-center">
              <img
                src={selectedImage}
                alt="Full Preview"
                className="max-w-full max-h-[calc(100vh-200px)] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {selectedTaskForGallery !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedTaskForGallery(null);
            setSelectedTaskImages([]);
            setShowGalleryLightbox(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Task Images ({selectedTaskImages.length})
              </h3>
              <button
                onClick={() => {
                  setSelectedTaskForGallery(null);
                  setSelectedTaskImages([]);
                  setShowGalleryLightbox(false);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {galleryLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#23A19B]"></div>
                </div>
              ) : selectedTaskImages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No images found for this task</p>
                </div>
              ) : (
                <SlideshowLightbox
                  images={selectedTaskImages.map(image => ({
                    src: getImageUrl(image.image || image.image_path),
                    alt: image.image_path || 'Task Image'
                  }))}
                  open={showGalleryLightbox}
                  onClose={() => setShowGalleryLightbox(false)}
                  showThumbnails={true}
                  theme="light"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedTaskImages.map((image, index) => (
                      <div
                        key={image.id || index}
                        className="relative bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
                        onClick={() => setShowGalleryLightbox(true)}
                      >
                        <img
                          src={getImageUrl(image.image || image.image_path)}
                          alt={`Task image ${index + 1}`}
                          className="w-full h-48 object-cover group-hover:opacity-75 transition-opacity"
                          onError={(e) => {
                            console.warn(
                              "Failed to load image:",
                              image.image || image.image_path,
                            );
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          {/* <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowGalleryLightbox(true);
                            }}
                            className="hidden group-hover:flex items-center justify-center w-12 h-12 bg-white rounded-full text-gray-900 hover:bg-gray-100"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                            </svg>
                          </button> */}
                        </div>
                      </div>
                    ))}
                  </div>
                </SlideshowLightbox>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskScheduler;
